
import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { calculateMarginFromMarkup, calculateMarkupFromMargin } from '@/utils/pricing-utils';
import { LineItemMarkupTier } from '@/types/line-item-markup-tiers';

interface MarkupTierFormProps {
  onAddTier: (tier: LineItemMarkupTier) => void;
}

const MarkupTierForm: React.FC<MarkupTierFormProps> = ({ onAddTier }) => {
  const [newTier, setNewTier] = useState<LineItemMarkupTier>({
    id: `temp-${Date.now()}`,
    min_amount: 0,
    max_amount: null,
    markup_percentage: 0,
    margin_percentage: 0
  });
  
  const handleMarkupChange = (value: string) => {
    if (value === '') { setNewTier({ ...newTier, markup_percentage: '' as any, margin_percentage: 0 }); return; }
    const markupValue = parseFloat(value) || 0;
    setNewTier({
      ...newTier, 
      markup_percentage: markupValue,
      margin_percentage: calculateMarginFromMarkup(markupValue)
    });
  };
  
  const handleMarginChange = (value: string) => {
    if (value === '') { setNewTier({ ...newTier, margin_percentage: '' as any, markup_percentage: 0 }); return; }
    const marginValue = parseFloat(value) || 0;
    setNewTier({
      ...newTier, 
      margin_percentage: marginValue,
      markup_percentage: calculateMarkupFromMargin(marginValue)
    });
  };
  
  const handleSubmit = () => {
    // Validate tier
    if (newTier.min_amount < 0) {
      return;
    }
    
    // Create a new tier object with all required properties
    const tierToAdd: LineItemMarkupTier = {
      id: `temp-${Date.now()}`,
      min_amount: newTier.min_amount,
      max_amount: newTier.max_amount,
      markup_percentage: newTier.markup_percentage,
      margin_percentage: newTier.margin_percentage
    };
    
    onAddTier(tierToAdd);
    
    // Reset new tier form
    setNewTier({
      id: `temp-${Date.now()}`,
      min_amount: 0,
      max_amount: null,
      markup_percentage: 0,
      margin_percentage: 0
    });
  };

  return (
    <div className="grid grid-cols-5 gap-2 mt-4">
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">
          Min Amount
        </label>
        <Input
          type="number"
          value={newTier.min_amount ?? ''}
          onChange={(e) => {
            const val = e.target.value;
            if (val === '') setNewTier({...newTier, min_amount: '' as any});
            else setNewTier({...newTier, min_amount: parseFloat(val) || 0});
          }}
          onBlur={() => { if ((newTier.min_amount as any) === '' || newTier.min_amount === undefined) setNewTier({...newTier, min_amount: 0}); }}
          step="0.01"
          min="0"
          placeholder="0.00"
        />
      </div>
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">
          Max Amount (optional)
        </label>
        <Input
          type="number"
          value={newTier.max_amount || ''}
          onChange={(e) => setNewTier({
            ...newTier, 
            max_amount: e.target.value ? parseFloat(e.target.value) : null
          })}
          step="0.01"
          min="0"
          placeholder="No limit"
        />
      </div>
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">
          Markup %
        </label>
        <Input
          type="number"
          value={newTier.markup_percentage}
          onChange={(e) => handleMarkupChange(e.target.value)}
          step="0.01"
          min="0"
          placeholder="0.00"
        />
      </div>
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">
          Margin %
        </label>
        <Input
          type="number"
          value={newTier.margin_percentage}
          onChange={(e) => handleMarginChange(e.target.value)}
          step="0.01"
          min="0"
          placeholder="0.00"
        />
      </div>
      <div className="flex items-end">
        <Button 
          type="button" 
          onClick={handleSubmit}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-1" /> Add Tier
        </Button>
      </div>
    </div>
  );
};

export default MarkupTierForm;
